<template>
  <div class="admin-page">
    <div class="admin-header">
      <h1>Pointe Applications</h1>
      <div class="header-actions">
        <router-link to="/pointe/admin/applications/import" class="btn-secondary">Import Historical</router-link>
      </div>
    </div>

    <div class="admin-nav">
      <router-link to="/pointe/admin/cohorts">Cohorts</router-link>
      <router-link to="/pointe/admin/consultations">Consultations</router-link>
      <router-link to="/pointe/admin/criteria">Criteria</router-link>
    </div>

    <!-- Filters -->
    <div class="filters">
      <div class="form-group">
        <label>Filter by Cohort</label>
        <select v-model="selectedCohort" class="form-input">
          <option value="">All Cohorts</option>
          <option v-for="c in cohorts" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>
      <div class="form-group">
        <label>Filter by Status</label>
        <select v-model="selectedStatus" class="form-input">
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="pending_payment">Pending Payment</option>
          <option value="awaiting_video">Awaiting Video</option>
          <option value="submitted">Submitted</option>
          <option value="under_review">Under Review</option>
          <option value="approved">Approved</option>
          <option value="needs_work">Needs Work</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>
      <div class="form-group search-group">
        <label>Search Dancer</label>
        <input v-model="searchQuery" type="text" class="form-input" placeholder="Name or email…" />
      </div>
    </div>

    <div v-if="loading" class="loading-wrap">
      <v-progress-circular indeterminate color="#1a1a1a"></v-progress-circular>
    </div>

    <div v-else-if="filtered.length === 0" class="empty-state">
      <p>No applications found.</p>
    </div>

    <div v-else class="applications-table">
      <table>
        <thead>
          <tr>
            <th>Dancer</th>
            <th>Cohort</th>
            <th>Status</th>
            <th>Payment</th>
            <th>Submitted</th>
            <th>Historical</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="app in filtered" :key="app.id">
            <td>
              <div class="dancer-name">{{ app.dancerName }}</div>
              <div class="dancer-email">{{ app.dancerEmail }}</div>
            </td>
            <td>{{ app.cohortName }}</td>
            <td><span class="status-badge" :class="app.status">{{ statusLabel(app.status) }}</span></td>
            <td><span class="payment-badge" :class="app.paymentStatus">{{ app.paymentStatus }}</span></td>
            <td>{{ formatDate(app.submittedAt) }}</td>
            <td>{{ app.isHistorical ? 'Yes' : '—' }}</td>
            <td>
              <router-link :to="'/pointe/admin/applications/' + app.id" class="btn-review">Review →</router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase'

export default {
  data () {
    return {
      applications: [],
      cohorts: [],
      loading: true,
      selectedCohort: '',
      selectedStatus: '',
      searchQuery: ''
    }
  },
  computed: {
    filtered () {
      return this.applications.filter(app => {
        if (this.selectedCohort && app.cohortId !== this.selectedCohort) return false
        if (this.selectedStatus && app.status !== this.selectedStatus) return false
        if (this.searchQuery) {
          const q = this.searchQuery.toLowerCase()
          if (!(app.dancerName || '').toLowerCase().includes(q) && !(app.dancerEmail || '').toLowerCase().includes(q)) return false
        }
        return true
      })
    }
  },
  async created () {
    const [appsSnap, cohortsSnap] = await Promise.all([
      db.collection('pointe-applications').orderBy('createdAt', 'desc').get(),
      db.collection('pointe-cohorts').orderBy('createdAt', 'desc').get()
    ])
    this.applications = appsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    this.cohorts = cohortsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    this.loading = false
  },
  methods: {
    statusLabel (status) {
      const map = {
        draft: 'Draft',
        pending_payment: 'Pending Payment',
        awaiting_video: 'Awaiting Video',
        submitted: 'Submitted',
        under_review: 'Under Review',
        approved: 'Approved',
        needs_work: 'Needs Work',
        refunded: 'Refunded'
      }
      return map[status] || status
    },
    formatDate (ts) {
      if (!ts) return '—'
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }
  }
}
</script>

<style scoped>
.admin-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
}

.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.admin-header h1 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 28px;
  color: #1a1a1a;
  margin: 0;
}

.header-actions { display: flex; gap: 12px; }

.admin-nav {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  border-bottom: 1px solid #e8e5e1;
  padding-bottom: 12px;
}

.admin-nav a { font-size: 14px; color: #666; text-decoration: none; }
.admin-nav a:hover { color: #1a1a1a; }

.filters {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  align-items: flex-end;
}

.filters .form-group { flex: 1; min-width: 180px; margin-bottom: 0; }
.search-group { flex: 2; }

.form-group label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #888;
  margin-bottom: 5px;
}

.form-input {
  width: 100%;
  border: 1px solid #e8e5e1;
  padding: 9px 12px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
  border-radius: 2px;
  box-sizing: border-box;
  color: #1a1a1a;
  background: #fff;
}

.form-input:focus { outline: none; border-color: #1a1a1a; }

.loading-wrap { padding: 40px; text-align: center; }
.empty-state { padding: 40px; text-align: center; color: #888; }

.applications-table { overflow-x: auto; }

table { width: 100%; border-collapse: collapse; font-size: 14px; }

th {
  text-align: left;
  padding: 10px 14px;
  border-bottom: 2px solid #e8e5e1;
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #888;
}

td {
  padding: 12px 14px;
  border-bottom: 1px solid #f0eeec;
  color: #1a1a1a;
  vertical-align: middle;
}

.dancer-name { font-weight: 600; }
.dancer-email { font-size: 12px; color: #888; }

.status-badge, .payment-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .04em;
  white-space: nowrap;
}

.status-badge.submitted { background: #e3f2fd; color: #1565c0; }
.status-badge.under_review { background: #fff8e1; color: #f57f17; }
.status-badge.approved { background: #e8f5e9; color: #2e7d32; }
.status-badge.needs_work { background: #fce4ec; color: #c62828; }
.status-badge.awaiting_video { background: #f3e5f5; color: #6a1b9a; }
.status-badge.pending_payment { background: #fff3e0; color: #e65100; }
.status-badge.draft { background: #f5f5f5; color: #616161; }
.status-badge.refunded { background: #f5f5f5; color: #9e9e9e; }

.payment-badge.paid { background: #e8f5e9; color: #2e7d32; }
.payment-badge.unpaid { background: #fff3e0; color: #e65100; }
.payment-badge.free_follow_up { background: #e8eaf6; color: #283593; }
.payment-badge.refunded { background: #f5f5f5; color: #9e9e9e; }

.btn-review {
  color: #1a1a1a;
  text-decoration: none;
  font-size: 13px;
  padding: 5px 12px;
  border: 1px solid #e8e5e1;
  border-radius: 2px;
  white-space: nowrap;
}

.btn-review:hover { border-color: #1a1a1a; }

.btn-secondary {
  background: #fff;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  padding: 9px 18px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
  cursor: pointer;
  border-radius: 2px;
  text-decoration: none;
  display: inline-block;
}
</style>
